import type { Config, PopoverDOM } from "driver.js";
import type { TFunction } from "i18next";

export const TOUR_POPOVER_CLASS = "ai-deck-tour-popover";

const DRIVER_DUMMY_ID = "driver-dummy-element";

const POPOVER_FLASH_CLASS = "ai-deck-tour-popover-flash";
// CSS の animation duration: shimmer 0.55s / glow 0.85s のうち長い方に合わせる
const POPOVER_FLASH_DURATION_MS = 850;

let flashTimerId: number | null = null;

/**
 * popover (モーダルウィンドウ) に「グロー + 斜めキラン」のフラッシュ効果を
 * 1 回再生する。Driver.js の onPopoverRender フックに関数参照として渡す
 * ため `PopoverDOM` を引数に取る。
 *
 * ステップ連続移動時に前回 timeout が残っていると新フラッシュ中に class が
 * 剥がされる race を起こすため、モジュールスコープに timer ID を保持して
 * 都度 clearTimeout する。終了時に wrapper が DOM から外れていれば触らない。
 */
export const flashPopover = (popover: PopoverDOM) => {
  if (flashTimerId !== null) window.clearTimeout(flashTimerId);
  const { wrapper } = popover;
  wrapper.classList.remove(POPOVER_FLASH_CLASS);
  // 強制 reflow で animation を確実に再開
  void wrapper.offsetWidth;
  wrapper.classList.add(POPOVER_FLASH_CLASS);
  flashTimerId = window.setTimeout(() => {
    if (wrapper.isConnected) {
      wrapper.classList.remove(POPOVER_FLASH_CLASS);
    }
    flashTimerId = null;
  }, POPOVER_FLASH_DURATION_MS);
};

/**
 * popover-only ステップ用の全画面サイズ dummy を先回りで設置する。
 * Driver.js は element 未指定時に画面中央 (top:50%, left:50%) に 0x0 の
 * dummy を生成し、stagePadding が加算されて中央に小さな円形 hole が出る。
 * 同 ID の要素が既存なら Driver.js はそれを再利用するため、ここで全画面要素を
 * 仕込んでおくことで hole がビューポート全体に広がり、画面が明るくなる。
 * Driver.js のクリーンアップ処理が同 ID を remove() するため後始末は不要。
 */
export const ensureFullscreenDummy = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(DRIVER_DUMMY_ID)) return;
  const dummy = document.createElement("div");
  dummy.id = DRIVER_DUMMY_ID;
  dummy.style.position = "fixed";
  dummy.style.top = "0";
  dummy.style.left = "0";
  dummy.style.width = "100vw";
  dummy.style.height = "100vh";
  dummy.style.pointerEvents = "none";
  dummy.style.opacity = "0";
  document.body.appendChild(dummy);
};

export const buildTourBaseConfig = (t: TFunction): Partial<Config> => ({
  showProgress: true,
  showButtons: ["next", "previous", "close"],
  nextBtnText: t("tour.buttons.next"),
  prevBtnText: t("tour.buttons.prev"),
  doneBtnText: t("tour.buttons.done"),
  progressText: t("tour.progressText", {
    current: "{{current}}",
    total: "{{total}}",
  }),
  popoverClass: TOUR_POPOVER_CLASS,
  allowClose: true,
  disableActiveInteraction: false,
  stagePadding: 8,
  stageRadius: 8,
  smoothScroll: true,
  animate: true,
});
